import React, { useMemo, useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getSpecialiteColor } from '@/types/calendar';

interface PlanningPanelCollapsedProps {
  isVisible: boolean;
  onExpand: () => void;
  todayCount: number;
  activeSpecialites: string[];
}

/**
 * Onglet plié du Planning (90px × 350px)
 * Affiche : icône + badge, "PLANNING" vertical, date du jour, points colorés
 */
const PlanningPanelCollapsed = ({
  isVisible,
  onExpand,
  todayCount,
  activeSpecialites,
}: PlanningPanelCollapsedProps) => {
  const [shouldBounce, setShouldBounce] = useState(false);

  // Animation bounce à l'arrivée sur le dashboard (décalé par rapport à Pilotage)
  useEffect(() => {
    if (isVisible) {
      // Délai plus long pour désynchroniser avec le panneau Pilotage
      const startTimer = setTimeout(() => setShouldBounce(true), 900);
      // Stopper l'animation après 1.5s
      const stopTimer = setTimeout(() => setShouldBounce(false), 2400);
      return () => {
        clearTimeout(startTimer);
        clearTimeout(stopTimer);
      };
    }
  }, []);

  const today = new Date();
  const dayNumber = format(today, 'd');
  const dayName = format(today, 'EEE', { locale: fr }).toUpperCase();
  const monthName = format(today, 'MMM', { locale: fr }).toUpperCase();

  // Limiter à 6 points max
  const displayedSpecialites = activeSpecialites.slice(0, 6);

  if (!isVisible) return null;

  return (
    <button
      data-miro-target="dashboard-planning-panel"
      onClick={onExpand}
      className={cn(
        // Position fixe bord droit, centré verticalement
        'fixed right-0 top-1/2 -translate-y-1/2 z-50',
        // Dimensions : 90px large × auto hauteur (min 350px)
        'w-[90px] min-h-[350px]',
        // Style visuel
        'bg-card/95 backdrop-blur-md',
        'border border-r-0 border-border/50 border-b-2 border-b-blue-500/40 border-t-2 border-t-amber-500/50 rounded-l-2xl',
        'shadow-2xl shadow-black/30',
        // Contenu centré en colonne
        'flex flex-col items-center py-6 gap-4',
        // Hover effect
        'hover:w-[100px] hover:bg-card transition-all duration-200',
        'group cursor-pointer',
        // Animation bounce à l'arrivée
        shouldBounce && 'animate-subtle-bounce'
      )}
    >
      {/* ═══ ICÔNE CALENDRIER + BADGE ═══ */}
      <div className="relative">
        <div className="p-3 bg-primary/10 rounded-xl group-hover:bg-primary/20 transition-colors">
          <Calendar className="w-7 h-7 text-primary group-hover:scale-110 transition-transform" />
        </div>
        {todayCount > 0 && (
          <span className="absolute -top-2 -right-2 w-6 h-6 bg-primary text-primary-foreground text-xs font-bold rounded-full flex items-center justify-center shadow-lg">
            {todayCount > 9 ? '9+' : todayCount}
          </span>
        )}
      </div>

      {/* ═══ TEXTE "PLANNING" VERTICAL ═══ */}
      <div
        className="text-sm font-bold tracking-widest text-white group-hover:text-white transition-colors"
        style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
      >
        PLANNING
      </div>

      {/* ═══ CHEVRON AFFORDANCE ═══ */}
      <ChevronLeft className="w-4 h-4 text-white/50 group-hover:text-white/80 transition-colors" />

      {/* ═══ SÉPARATEUR ═══ */}
      <div className="w-10 h-px bg-border/50" />

      {/* ═══ DATE DU JOUR ═══ */}
      <div className="flex flex-col items-center">
        <span className="text-3xl font-black text-foreground leading-none">
          {dayNumber}
        </span>
        <span className="text-xs font-medium text-muted-foreground mt-1">
          {dayName}
        </span>
        <span className="text-[10px] font-medium text-muted-foreground/70">
          {monthName}
        </span>
      </div>

      {/* ═══ POINTS COLORÉS DES SPÉCIALITÉS ACTIVES ═══ */}
      {displayedSpecialites.length > 0 && (
        <>
          <div className="w-10 h-px bg-border/50" />
          <div className="flex flex-wrap justify-center gap-1.5 px-2 max-w-[70px]">
            {displayedSpecialites.map((specialite, idx) => {
              const colors = getSpecialiteColor(specialite);
              return (
                <div
                  key={`${specialite}-${idx}`}
                  className={cn('w-3 h-3 rounded-full', colors.dot)}
                  title={specialite}
                />
              );
            })}
          </div>
        </>
      )}
    </button>
  );
};

export default PlanningPanelCollapsed;
